import { v4 as uuidv4 } from 'uuid';
import { ContextUtility } from "./context.utility";
import { RestUtility } from "./rest.utility";
import { UrlUtility } from "./url.utility";

// TODO: this cannot be included for node due to jQuery in one of the methods. Somehow make sure the jQuery using method is ignored and not included in the node build

function addProductToCartByFrame(productCode: string, productPageUrl: string = "/ItemDetail") {
    return new Promise<void>((resolve, reject) => {
        // add product with the above product code to the cart
        let productPageUrlFull = UrlUtility.getUrlStringWithParams(productPageUrl, { iProductCode: productCode });
        let jFrame = jQuery("<iframe src='" + productPageUrlFull + "'></iframe>");
        jFrame.on("load", function () {
            let frameElement = <HTMLIFrameElement>jFrame[0];
            let contentWindow = frameElement.contentWindow;

            let jItemAdded = contentWindow.jQuery("[id$=_ItemAdded]");
            if (jItemAdded.length > 0) {
                // redirect to the appropriate cart page
                resolve();
            } else {
                // initiate an add to cart
                let jAddToCart = contentWindow.jQuery("[id$=_AddToCart]");
                jAddToCart[0].click();
            }
        });
        jFrame.appendTo("body");
        jFrame.hide();
    });
}

async function addProductToCart(productCode: string, id?: string) {
    let selectedId = id || ContextUtility.getSelectedImisId();

    // get product item details
    let itemDetails: any = await RestUtility.restApiGetData("/api/item/" + productCode);
    let partyDetails: any = await RestUtility.restApiGetData("/api/party/" + selectedId);
    if (itemDetails == null || partyDetails == null) {
        throw new Error(`Failed to get item details for product code: ${productCode} and user with ID: ${selectedId}`);
    }

    // setup payload for cart
    // add delivery details if not provided
    let cartPayload = await generateCartPayload(selectedId, productCode);
    if (cartPayload.ComboOrder.Order.Delivery.$values.length == 0) {
        // get the mailing address for the user
        let mailingAddress = partyDetails.Addresses.find(address => address.CommunicationPreferences.some(pref => pref.Reason == "mail"));
        if (mailingAddress == null) {
            throw new Error(`Failed to get mailing address for user with ID: ${selectedId}`);
        }

        cartPayload.ComboOrder.Order.Delivery.$values.push({
            Address: mailingAddress
        });
    }

    // update pricing for the newly added product to the existing cart
    let orderData = await RestUtility.restApiSendData("/api/order/_execute", {
        "$type": "Asi.Soa.Commerce.DataContracts.OrderPriceUpdateRequest, Asi.Contracts",
        "EntityTypeName": "Order",
        "OperationName": "UpdatePricing",
        "Order": cartPayload.ComboOrder.Order
    }, "POST", true);
    if (orderData == null) {
        throw new Error(`Failed to update pricing for the cart for user with ID: ${selectedId}`);
    }
    cartPayload.ComboOrder.Order = orderData;

    let cartId = cartPayload.CartId;
    let cartUpdateResponse = await RestUtility.restApiSendData("/api/cart/" + cartId, cartPayload, "PUT");
    if (cartUpdateResponse == null) {
        throw new Error(`Failed to update cart for user with ID: ${selectedId} and cart ID: ${cartId}`);
    }
}

async function getCartById(id: string, rawResponse: boolean = false) {
    return RestUtility.restApiGetData("/api/cart", {
        userid: id, updatedby: id
    }, rawResponse);
}

async function deleteCartById(id: string) {
    let cartResponse = await getCartById(id);
    let cartItem: any = cartResponse != null ? cartResponse.Items[0] : null;
    if (cartItem != null) {
        let cartId = cartItem.CartId;

        let isSuccess = await RestUtility.restApiDeleteData("/api/cart/" + cartId);
        if (!isSuccess) {
            throw new Error(`Failed to delete cart for user with ID: ${id} and cart ID: ${cartId}`);
        }
    }
}

async function ensureCartById(id: string) {
    let cartData = null;
    let currentDate = new Date();

    let cartResponse: any = await getCartById(id, true);
    if (cartResponse == null) {
        throw new Error(`Failed to get cart for user with ID: ${id}`);
    }
    if (cartResponse.Items.$values.length > 0) {
        cartData = cartResponse.Items.$values[0];
    } else {
        // create an empty cart
        let addCartPayload = {
            $type: "Asi.Soa.Commerce.DataContracts.CartData, Asi.Contracts",
            UserId: id,
            ComboOrder: {
                $type: "Asi.Soa.Commerce.DataContracts.ComboOrderData, Asi.Contracts"
            },
            UpdateInformation: {
                $type: "Asi.Soa.Core.DataContracts.EntityUpdateInformationData, Asi.Contracts",
                CreatedBy: id,
                CreatedOn: currentDate,
                UpdatedBy: id,
                UpdatedOn: currentDate
            }
        };
        cartData = await RestUtility.restApiSendData("/api/cart", addCartPayload, "POST", true);
        if (cartData == null) {
            throw new Error(`Failed to create cart for user with ID: ${id}`);
        }
    }

    return cartData;
}

async function generateCartPayload(id: string, productCode: string) {
    // ensure the cart object exists
    let cartData = await ensureCartById(id);
    let orderData = cartData.ComboOrder.Order;
    // have to check delivery property because by default an empty cart will always contain the Currency and Lines property
    if (orderData.Delivery == null) {
        // create the order object
        orderData = {
            BillToCustomerParty: {
                PartyId: id,
            },
            Delivery: {
                $values: []
            },
            Lines: {
                $values: []
            },
            OriginatorCustomerParty: {
                PartyId: id,
            },
            SoldToCustomerParty: {
                PartyId: id,
            }
        };

        cartData.ComboOrder.Order = orderData;
    }

    // add the line item to the cart
    let linePayload = {
        OrderLineId: uuidv4(),
        Item: {
            ItemCode: productCode,
            ItemId: productCode
        },
        QuantityOrdered: {
            Amount: 1
        },
        QuantityShipped: {
            Amount: 1
        }
    };
    orderData.Lines.$values.push(linePayload);

    return cartData;
}

export {
    addProductToCartByFrame,
    addProductToCart,
    getCartById,
    deleteCartById,
    ensureCartById,
    generateCartPayload,
};
